import React, { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import styles from '../floatingPanel/floatingResizablePanel.module.scss';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import CustomIcon from '@/components/ui/CustomIcon';

type Props = {
  title: ReactNode;
  children?: ReactNode;
  onClose?: () => void;
  primaryColor?: string;
  secondaryColor?: string;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
};

type MobileViewport = {
  height: string | number;
  width: string | number;
  offsetTop: number;
  offsetLeft: number;
};

const FloatingResizablePanel = ({
  onClose,
  children,
  title,
  primaryColor,
  secondaryColor,
  isFullscreen = false,
  onToggleFullscreen,
}: Props): ReactElement => {
  const getMobileViewport = (): MobileViewport => {
    if (window.innerWidth >= 768) {
      return {
        height: '100%',
        width: '100%',
        offsetTop: 0,
        offsetLeft: 0,
      };
    }

    if (window.visualViewport) {
      return {
        height: window.visualViewport.height,
        width: window.visualViewport.width,
        offsetTop: window.visualViewport.offsetTop,
        offsetLeft: window.visualViewport.offsetLeft,
      };
    }

    return {
      height: window.innerHeight,
      width: '100%',
      offsetTop: 0,
      offsetLeft: 0,
    };
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileViewport, setMobileViewport] = useState<MobileViewport>(getMobileViewport);

  const [translateY, setTranslateY] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Small delay to ensure the browser has painted the initial state
    const timer = setTimeout(() => setIsReady(true), 20);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = (): void => {
    if (isMobile) {
      setIsClosing(true);
      setTimeout(() => {
        if (onClose) onClose();
      }, 300);
    } else {
      setIsAnimatingOut(true);
      setTimeout(() => {
        if (onClose) onClose();
      }, 300);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const syncViewport = (): void => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setMobileViewport(getMobileViewport());
    };

    syncViewport();

    const visualViewport = window.visualViewport;

    window.addEventListener('resize', syncViewport);
    if (visualViewport) {
      visualViewport.addEventListener('resize', syncViewport);
      visualViewport.addEventListener('scroll', syncViewport);
    }

    return () => {
      window.removeEventListener('resize', syncViewport);
      if (visualViewport) {
        visualViewport.removeEventListener('resize', syncViewport);
        visualViewport.removeEventListener('scroll', syncViewport);
      }
    };
  }, []);

  const handleTouchStart = (event: React.TouchEvent): void => {
    if (!isMobile) return;
    const touch = event.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
    setIsSwiping(true);
  };

  const handleTouchMove = (event: React.TouchEvent): void => {
    if (!isMobile || !touchStart.current) return;
    const touch = event.touches[0];
    const deltaY = touch.clientY - touchStart.current.y;

    if (deltaY > 0) {
      setTranslateY(deltaY);
    }
  };

  const handleTouchEnd = (): void => {
    if (!isMobile || !touchStart.current) return;

    if (translateY > 150) {
      setIsClosing(true);
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 300);
    } else {
      setTranslateY(0);
      setIsSwiping(false);
      touchStart.current = null;
    }
  };

  const customColorsStyle = {
    ...(primaryColor ? { '--primary-color': primaryColor } : {}),
    ...(secondaryColor ? { '--secondary-color': secondaryColor } : {}),
  } as React.CSSProperties;

  const style: React.CSSProperties = {
    ...(isMobile
      ? {
        height: mobileViewport.height,
        top: mobileViewport.offsetTop,
        left: mobileViewport.offsetLeft,
        right: 'auto',
        bottom: 'auto',
        width: mobileViewport.width,
        maxWidth: mobileViewport.width,
        transform: isClosing ? `translateY(100%)` : `translateY(${translateY}px)`,
        transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
      }
      : {}),
    ...customColorsStyle,
  };

  return (
    <>
      {createPortal(
        <div
          className={classNames(styles.backdrop, {
            [styles.visible]: isMobile || isFullscreen,
          })}
          style={{
            opacity: isMobile
              ? isClosing
                ? 0
                : Math.max(0, 1 - translateY / 300)
              : isFullscreen
                ? 0.5
                : 0,
          }}
          onClick={handleClose}
        />,
        document.body
      )}
      {createPortal(
        <div
          className={classNames(styles['animation-wrapper'], {
            [styles.fullscreen]: isFullscreen && !isMobile,
          })}
          style={customColorsStyle}
        >
          <div
            className={classNames(styles.panel, {
              [styles.fullscreen]: isFullscreen && !isMobile,
              [styles['is-mobile']]: isMobile,
              [styles.visible]: (isMobile && !isClosing) || (!isMobile && isReady && !isAnimatingOut),
              [styles.exiting]: !isMobile && isAnimatingOut,
            })}
            style={style}
          >
            <div
              className={styles.head}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className={styles.title}>{title}</div>
              {!isMobile && onClose && (
                <div className={styles['header-actions']}>
                  <div className={styles['menu-container']} ref={menuRef}>
                    <button
                      className={styles['dots-button']}
                      onClick={() => setIsMenuOpen(!isMenuOpen)}
                    >
                      <CustomIcon name="dots" width={20} height={20} />
                    </button>
                    {isMenuOpen && (
                      <div className={styles.menu}>
                        <button
                          className={styles['menu-item']}
                          onClick={() => {
                            if (onToggleFullscreen !== undefined) {
                              onToggleFullscreen();
                            }
                            setIsMenuOpen(false);
                          }}
                        >
                          <CustomIcon
                            name={isFullscreen ? 'collapse' : 'expand'}
                            width={16}
                            height={16}
                          />
                          {isFullscreen ? 'Reducir ventana' : 'Ampliar ventana'}
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      handleClose();
                    }}
                    className={styles['close-button']}
                  >
                    <CustomIcon name="close" width={20} height={20} />
                  </button>
                </div>
              )}
            </div>
            <div className={styles.content}>{children}</div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default FloatingResizablePanel;
